import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, Observable, of } from 'rxjs';
import { mergeMap, switchMap, tap } from 'rxjs/operators';
import { EmployeeEventType } from 'src/app/enums/employee-event-type.enum';
import { EmployeeStatus } from 'src/app/enums/employee-status.enum';
import { EmployeeDeleteDto } from 'src/app/models/employee-delete.model';
import { IResultModel, Result, ResultModel } from 'src/app/models/general/result.model';
import { UserProfile } from 'src/app/models/user-profile.model';
import { AuthService, EmployeeService, StateService } from 'src/app/services';
import { ConfirmDialogComponent, IConfirmDialogModel } from 'src/app/shared/components/dialogs';
import { HtmlListItem } from 'src/app/shared/models/html-list-item.model';
import { ErrorMessageService } from 'src/app/shared/services';
import { EmployeeState } from 'src/app/store/employee.state';

@UntilDestroy()
@Component({
  selector: 'app-employee',
  templateUrl: './employee.component.html',
  styleUrls: ['./employee.component.scss']
})
export class EmployeeComponent implements OnInit {
  public model$: Observable<Result<boolean>>;
    
  public employeeStatus = EmployeeStatus;

  constructor(
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private employeeStore: EmployeeState,
    private stateService: StateService,
    private employeeService: EmployeeService,
    private msgService: ErrorMessageService,
    private authService: AuthService,
    private dialog: MatDialog,
    )
  { }

  public ownProfile = false;
  public isLoggedInSA = false;  
  public isLoggedInAdmin = false;
  public isLoggedInAssistant = false;
  
  public get isDeleteInProgress(): boolean {
    return this.userProfile?.employee?.employeeEvent?.eventType == EmployeeEventType.Delete;
  }

  public get isSelf(): boolean {
    return this.userProfile?.id === this.stateService.state.me?.value.id;
  }

  public get isEmployeeDeleted(): boolean {
    return this.userProfile?.employee?.employeeStatus === EmployeeStatus.Deleted;
  }

  public userProfile: UserProfile;

  // if the user is an sa, these panels should not be visible because they are not relevant (sa has no employee entity)
  public get selectedUserHasEmployee(): boolean {    
    return !!this.userProfile?.employee;
  }

  ngOnInit(): void {    
    this.model$ = this.employeeStore.employee.obs$.pipe(
      mergeMap(userProfile => {
        if (userProfile)
        {

          this.userProfile = userProfile;

          //if the logged in user is an SA or Admin
          this.ownProfile = userProfile.id === this.stateService.state.me.value.id;
          this.isLoggedInSA = this.stateService.state.isSuperAdmin;
          this.isLoggedInAdmin = this.stateService.state.isAdmin;
          this.isLoggedInAssistant = this.stateService.state.isAssistant;
          
          return this.employeeService.isBossOf(userProfile.id);
        }
        else
          return of(null);
      }),
      tap(result => {
        if (result) {
          this.employeeStore.isBoss.value = result.data;
        }
      })
    );    

    this.route.params
    .pipe(untilDestroyed(this))
    .subscribe(params => {
        this.employeeStore.loadEmployee(params['employeeId']).subscribe();
    });
            
  } 
      
  // public isAdminProfile(user: UserProfile): boolean {    
  //   return this.stateService.state.isSuperAdmin && user.id === this.stateService.state.me.value.id;
  // }

  public markForDelete(): void {
      const dto = {
        employeeId: this.userProfile.employee.id
      } as EmployeeDeleteDto;
            
      var lines = this.getDeleteDialogLines();

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: "600px",
        data: {
            title: this.translate.instant('TEXTS.DELETE_USER_TITLE'),
            lines: lines,
            confirmButtonConfig: {name: this.translate.instant('GENERAL.DELETE'), color: 'warn'},
        } as IConfirmDialogModel
      });

      dialogRef.afterClosed().subscribe( (dialogResult: boolean) => {
        if (dialogResult)
        {
          this.authService.deleteEmployee(dto).subscribe({next: () => {
            this.msgService.showSnackBarMessage('deleteEmployeeSuccessful');
            this.router.navigate(['/']);
          }, error: (err: IResultModel<ResultModel>) => {
            this.msgService.handleError<ResultModel>(err);
          }});
        }
     });

      
  }

  public getDeleteDialogLines(): HtmlListItem[] {
    let lines: HtmlListItem[] = [];

    lines.push({text: this.translate.instant('TEXTS.DELETE_EMPLOYEE_DIALOG_LINE1')} as HtmlListItem);
    lines.push({text: this.translate.instant('TEXTS.DELETE_EMPLOYEE_DIALOG_LINE2'), classList: ['text-danger']} as HtmlListItem);
    lines.push({text: this.translate.instant('TEXTS.DELETE_EMPLOYEE_DIALOG_LINE3')} as HtmlListItem);
    lines.push({text: this.translate.instant('TEXTS.DELETE_EMPLOYEE_DIALOG_LINE4')} as HtmlListItem);
    lines.push({text: this.translate.instant('TEXTS.DELETE_EMPLOYEE_DIALOG_LINE_LAST'), classList: ['text-danger']} as HtmlListItem);

    return lines;
  }

  public getDeleteMessage(): string {
    let msg = '';

    if (this.userProfile.company == 'Magánszemélyek')
    {
      msg = this.translate.instant('TEXTS.DELETE_EMPLOYEE_PRIVATE_MESSAGE');
    }
    else
    {
        msg = this.translate.instant('TEXTS.DELETE_EMPLOYEE_COMPANY_MESSAGE');
    }

    msg += this.translate.instant('TEXTS.DELETE_CONFIRMATION_QUESTION');

    return msg;
  }

  public onEmployeeStatusChange(newStatus: EmployeeStatus): void {
    const isInactive = newStatus === EmployeeStatus.Inactive;
    const action = isInactive ? 'inactivate' : 'activate';
    
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "600px",
      data: {
        title: this.translate.instant(`EMPLOYEE.${action.toUpperCase()}_EMPLOYEE_TITLE`),
        message: this.translate.instant(`EMPLOYEE.${action.toUpperCase()}_EMPLOYEE_MESSAGE`),
        confirmButtonConfig: {
          name: this.translate.instant('GENERAL.CONFIRM'), 
          color: isInactive ? 'warn' : 'primary'
        },
      } as IConfirmDialogModel
    });

    dialogRef.afterClosed().subscribe((dialogResult: boolean) => {
      if (dialogResult) {
        this.authService.setEmployeeInactive(this.userProfile.employee.id, isInactive).subscribe({
          next: () => {
            this.msgService.showSnackBarMessage('successfulAction');
            this.employeeStore.loadEmployee(this.userProfile.id).subscribe();
          }, 
          error: (err: IResultModel<ResultModel>) => {
            this.msgService.handleError<ResultModel>(err);
          }
        });
      } else {
        // Revert dropdown to original value
        this.employeeStore.loadEmployee(this.userProfile.id).subscribe();
      }
    });
  }

  public cancelDelete(): void {
    this.authService.cancelDeleteEmployee(this.userProfile.employee.id).subscribe({next: () => {
      this.msgService.showSnackBarMessage('successfulAction');

      this.employeeStore.loadEmployee(this.userProfile.id).subscribe();
    }, error: () => {
      this.msgService.showSnackBarMessage('unsuccessfulAction');
    }});
  }

}
